/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TextField,
    MenuItem,
    InputAdornment,
    CircularProgress,
    IconButton,
    Tooltip,
    Chip,
} from "@mui/material";
import {
    FilterList,
    Refresh,
    PlayArrow,
    CheckCircleOutline,
    ErrorOutline,
    BlockOutlined,
    OpenInNew,
    Email,
    Sms,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchDunningEvents,
    runDunningNow,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectDunningEvents,
    selectIsLoadingDunningEvents,
    selectDunningEventsError,
    selectDunningEventsCursor,
    selectIsRunningDunning,
    selectLastDunningRun,
} from "../../redux/selectors/collectSelector";
import {
    DunningEventStatus,
    IDunningEvent,
} from "../../utility/types/collectTypes";
import { colors } from "../../utility/types/constant";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectInfoBanner,
    FlexBetween,
} from "./styles";

const STATUS_OPTIONS: Array<{
    value: "all" | DunningEventStatus;
    label: string;
}> = [
        { value: "all", label: "All outcomes" },
        { value: "sent", label: "Sent" },
        { value: "failed", label: "Failed" },
        { value: "skipped", label: "Skipped" },
    ];

function formatDateTime(value?: string): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return value;
    }
}

// Plain-English messages for the delivery error codes carriers/Twilio return,
// so owners see something they can act on instead of a raw code like
// "twilio:30005". Unknown codes fall back to a generic friendly line.
const DELIVERY_ERROR_MESSAGES: Record<string, string> = {
    "30003": "The phone was unreachable — it may be switched off or out of signal.",
    "30004": "The carrier blocked this message.",
    "30005": "This number appears to be disconnected or can no longer receive texts.",
    "30006": "This looks like a landline or a number that can't receive texts.",
    "30007": "The carrier filtered this message as spam.",
    "30008": "The message couldn't be delivered (carrier reported an unknown error).",
    "21211": "This phone number isn't valid.",
    "21610": "This contact has opted out of texts (they replied STOP).",
    "21614": "This number can't receive texts — it isn't a mobile number.",
};

// Turn a stored delivery error (e.g. "twilio:30005" or "30005") into a friendly
// sentence. If the value is already human-readable, it's shown unchanged.
function friendlyDeliveryError(raw?: string | null): string | null {
    if (!raw) return null;
    const text = String(raw).trim();
    let codeSource: string | null = null;
    if (text.toLowerCase().startsWith("twilio:")) {
        codeSource = text.slice(text.indexOf(":") + 1);
    } else if (/^\d{4,6}$/.test(text)) {
        codeSource = text;
    }
    if (codeSource !== null) {
        const digits = (codeSource.match(/\d{4,6}/) || [])[0];
        if (digits && DELIVERY_ERROR_MESSAGES[digits]) {
            return DELIVERY_ERROR_MESSAGES[digits];
        }
        return "The message couldn't be delivered by the carrier.";
    }
    return text;
}

const STATUS_PALETTE: Record<string, { bg: string; color: string }> = {
    sent: { bg: "rgba(30,193,149,0.12)", color: "#0f7a5b" },
    failed: { bg: "rgba(220, 38, 38, 0.12)", color: "#b91c1c" },
    skipped: { bg: "rgba(107, 114, 128, 0.15)", color: "#374151" },
};

const STATUS_FALLBACK = { bg: "rgba(107, 114, 128, 0.15)", color: "#374151" };

const STATUS_ICON: Record<string, React.ReactNode> = {
    sent: <CheckCircleOutline sx={{ fontSize: 16 }} />,
    failed: <ErrorOutline sx={{ fontSize: 16 }} />,
    skipped: <BlockOutlined sx={{ fontSize: 16 }} />,
};

function normalizeStatus(s?: string): string {
    return (s || "").toLowerCase();
}

const CollectActivity: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();
    const businessId = useSelector(selectActiveBusinessId);
    const events = useSelector(selectDunningEvents);
    const isLoading = useSelector(selectIsLoadingDunningEvents);
    const error = useSelector(selectDunningEventsError);
    const nextCursor = useSelector(selectDunningEventsCursor);
    const isRunning = useSelector(selectIsRunningDunning);
    const lastRun = useSelector(selectLastDunningRun);

    const [statusFilter, setStatusFilter] = useState<string>("all");

    const queryPayload = useMemo(() => {
        const p: any = { limit: 50 };
        if (statusFilter !== "all") p.status = statusFilter;
        return p;
    }, [statusFilter]);

    useEffect(() => {
        if (!businessId) return;
        dispatch(fetchDunningEvents(queryPayload));
    }, [dispatch, businessId, queryPayload]);

    const onRun = async () => {
        if (!businessId) return;
        const result: any = await dispatch(runDunningNow(undefined));
        if (result?.meta?.requestStatus === "fulfilled") {
            const r = result.payload || {};
            toast.success(
                `Dunning run complete — ${r.remindersSent ?? 0} sent, ${r.remindersSkipped ?? 0} skipped${r.remindersFailed ? `, ${r.remindersFailed} failed` : ""}.`,
            );
        } else {
            toast.error("Couldn't run dunning. Try again in a moment.");
        }
        dispatch(fetchDunningEvents(queryPayload));
    };

    return (
        <CollectPageWrapper>
            <Box
                sx={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    padding: { xs: "20px 12px", md: "32px 24px" },
                }}
            >
                <FlexBetween style={{ marginBottom: 8 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)",
                                color: colors.primary,
                            }}
                        >
                            Reminder activity
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#6b7280",
                                fontSize: "0.9rem",
                                mt: 0.5,
                                maxWidth: 720,
                            }}
                        >
                            Every reminder we've ever sent, plus failures and skips with
                            the reason. The dunning engine runs automatically every day.
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <CollectGhostButton
                            startIcon={<Refresh fontSize="small" />}
                            onClick={() => dispatch(fetchDunningEvents(queryPayload))}
                            disabled={isLoading}
                        >
                            Refresh
                        </CollectGhostButton>
                        <CollectPrimaryButton
                            startIcon={
                                isRunning ? (
                                    <CircularProgress
                                        size={16}
                                        sx={{ color: "#fff" }}
                                    />
                                ) : (
                                    <PlayArrow fontSize="small" />
                                )
                            }
                            onClick={onRun}
                            disabled={isRunning || !businessId}
                        >
                            {isRunning ? "Running…" : "Run dunning now"}
                        </CollectPrimaryButton>
                    </Box>
                </FlexBetween>

                {lastRun && (
                    <CollectInfoBanner tone="ok">
                        <span>
                            Last run: {lastRun.processed} processed · {lastRun.sent}{" "}
                            sent · {lastRun.skipped} skipped · {lastRun.failed} failed
                        </span>
                    </CollectInfoBanner>
                )}
                {error && (
                    <CollectInfoBanner tone="warn">
                        <span>{error}</span>
                    </CollectInfoBanner>
                )}

                <CollectSectionCard>
                    <Box sx={{ mb: 2.5, maxWidth: 320 }}>
                        <TextField
                            size="small"
                            select
                            fullWidth
                            label="Outcome"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FilterList fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    {isLoading && events.length === 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress sx={{ color: colors.mainSecondary }} />
                        </Box>
                    ) : events.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>No activity yet</CollectEmptyTitle>
                            <CollectEmptyHint>
                                Once campaigns kick in (or you manually send a reminder
                                from an invoice), every email and SMS will show up here.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <CollectTableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <CollectTableHeadCell>When</CollectTableHeadCell>
                                        <CollectTableHeadCell>Trigger</CollectTableHeadCell>
                                        <CollectTableHeadCell>Channel</CollectTableHeadCell>
                                        <CollectTableHeadCell>Recipient</CollectTableHeadCell>
                                        <CollectTableHeadCell>Subject</CollectTableHeadCell>
                                        <CollectTableHeadCell>Outcome</CollectTableHeadCell>
                                        <CollectTableHeadCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {events.map((e: IDunningEvent) => (
                                        <TableRow key={e._id} hover>
                                            <CollectTableBodyCell>
                                                {formatDateTime(e.sentAt)}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                <Chip
                                                    size="small"
                                                    label={
                                                        normalizeStatus(e.trigger) === "manual"
                                                            ? "Manual"
                                                            : "Scheduled"
                                                    }
                                                    sx={{
                                                        backgroundColor:
                                                            normalizeStatus(e.trigger) === "manual"
                                                                ? "rgba(59, 130, 246, 0.1)"
                                                                : "rgba(107, 114, 128, 0.12)",
                                                        color:
                                                            normalizeStatus(e.trigger) === "manual"
                                                                ? "#1e40af"
                                                                : "#374151",
                                                    }}
                                                />
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    {normalizeStatus(e.channel) === "email" ? (
                                                        <Email sx={{ fontSize: 16, color: "#6b7280" }} />
                                                    ) : (
                                                        <Sms sx={{ fontSize: 16, color: "#6b7280" }} />
                                                    )}
                                                    {normalizeStatus(e.channel)}
                                                </Box>
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {e.recipient || "—"}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {e.subject ? (
                                                    <span style={{ fontStyle: "italic" }}>
                                                        "{e.subject}"
                                                    </span>
                                                ) : (
                                                    "—"
                                                )}
                                                {(e.errorMessage || e.error) && (
                                                    <Box
                                                        sx={{
                                                            color: "#b91c1c",
                                                            fontSize: "0.78rem",
                                                            mt: 0.3,
                                                        }}
                                                    >
                                                        {friendlyDeliveryError(
                                                            e.errorMessage || e.error,
                                                        )}
                                                    </Box>
                                                )}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {(() => {
                                                    const key = normalizeStatus(e.status);
                                                    const palette = STATUS_PALETTE[key] || STATUS_FALLBACK;
                                                    const icon = STATUS_ICON[key];
                                                    return (
                                                        <Chip
                                                            size="small"
                                                            icon={icon as React.ReactElement}
                                                            label={key || "unknown"}
                                                            sx={{
                                                                backgroundColor: palette.bg,
                                                                color: palette.color,
                                                                fontWeight: 700,
                                                            }}
                                                        />
                                                    );
                                                })()}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                {e.invoiceId && (
                                                    <Tooltip title="Open invoice">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/collect/invoices/${e.invoiceId}`,
                                                                )
                                                            }
                                                        >
                                                            <OpenInNew fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </CollectTableBodyCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CollectTableContainer>
                    )}

                    {nextCursor && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <CollectGhostButton
                                onClick={() =>
                                    dispatch(
                                        fetchDunningEvents({
                                            ...queryPayload,
                                            cursor: nextCursor,
                                        }),
                                    )
                                }
                                disabled={isLoading}
                            >
                                {isLoading ? "Loading…" : "Load next page"}
                            </CollectGhostButton>
                        </Box>
                    )}
                </CollectSectionCard>
            </Box>
        </CollectPageWrapper>
    );
};

export default CollectActivity;
